import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyledOrdersList } from "./styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getOrderPickupAddress } from "utils/returnAddress";
import { getEncipherencode } from "utils/encipherencode";
import { deliveryStatusCheck, ordersList } from "utils/api";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addEmail,
  addPhone,
  getEmail,
  getPhone,
} from "features/user/userSlice";
import {
  addOrders,
  getSelectedOrder,
  updateSelectedOrder,
} from "features/orders/orderSlice";
import {
  addRefundJSON,
  addRefundSource,
  addReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import {
  addAddress,
  addShipmentMethod,
  updateServiceability,
} from "features/address/addresSlice";
import { addUploadMediaIds } from "features/product/productSlice";
import {
  shouldForwardRequestTarget,
  stripVersionPrefix,
  versionedPath,
} from "utils/routes";
import { appendWarrantyClaimParam } from "utils/warrantyClaim";
import { V2OrdersBoneyardLoader } from "components/v2/Loaders/BoneyardLoaders";
import OrderCard from "./OrderCard";
import OrdersSearch from "./OrdersSearch";
import { filterOrdersByQuery, sortOrdersByEligibility } from "./utils";
import PageHeader from "components/v2/Common/PageHeader";
import PageIntro from "components/v2/Common/PageIntro";
import {
  isSingleRequestPerOrderMerchant,
  singleRequestPerOrderNotice,
} from "utils/merchantPolicies";

function OrdersList({ setLoginLoader }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const userPhone = useSelector(getPhone) || params.get("p");
  const userEmail = useSelector(getEmail) || params.get("e");
  const selectedOrder = useSelector(getSelectedOrder);

  const [allOrders, setAllOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [apiLoadError, setApiLoadError] = useState(false);
  const [finishStatus, setfinishStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchStuck, setIsSearchStuck] = useState(false);
  const searchShellRef = useRef(null);

  const orderId = params.get("oid");
  const rid = params.get("rid");
  const nextTarget = params.get("nextTarget");
  const normalizedNextTarget = stripVersionPrefix(`/${nextTarget || ""}`)
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  const accessToken = params.get("access_token");

  useEffect(() => {
    const updateSearchStickiness = () => {
      if (!searchShellRef.current) {
        return;
      }
      const rect = searchShellRef.current.getBoundingClientRect();
      setIsSearchStuck(rect.top <= 72 && window.scrollY > 0);
    };

    updateSearchStickiness();
    window.addEventListener("scroll", updateSearchStickiness, {
      passive: true,
    });
    window.addEventListener("resize", updateSearchStickiness);

    return () => {
      window.removeEventListener("scroll", updateSearchStickiness);
      window.removeEventListener("resize", updateSearchStickiness);
    };
  }, []);

  const resetToLogin = () => {
    // sessionIframe removed intentionally; legacy session sync disabled.
    // const iframe = document.getElementById("sessionIframe");
    // iframe?.contentWindow?.postMessage(
    //   { phone: null, email: null, topic: "PRAGMA" },
    //   "*",
    // );
    dispatch(addPhone(null));
    dispatch(addEmail(null));

    let url = versionedPath("/");
    if (encipherencode) {
      url += `?encipherencode=${encipherencode}`;
    }
    navigate(url);
  };

  const buildForwardedTargetUrl = (targetPath) => {
    const forwardedParams = new URLSearchParams(params.toString());

    if (encipherencode && !forwardedParams.has("encipherencode")) {
      forwardedParams.set("encipherencode", encipherencode);
    }

    forwardedParams.set("rid", String(rid).trim());

    const queryString = forwardedParams.toString();
    const url = versionedPath(targetPath);

    return queryString ? `${url}?${queryString}` : url;
  };

  const onBackButtonEvent = (event) => {
    event.preventDefault();
    if (finishStatus) {
      return;
    }

    setfinishStatus(true);
    if (selectedOrder !== null && Object.keys(selectedOrder).length > 0) {
      dispatch(updateSelectedOrder({}));
      return;
    }

    resetToLogin();
  };

  const updateOrderDetails = (order) => {
    dispatch(updateSelectedOrder(order));
    dispatch(addAddress(getOrderPickupAddress(order)));
    window.scrollTo(0, 0);

    const goToProducts = () => {
      const productParams = appendWarrantyClaimParam(params);
      if (encipherencode) {
        productParams.set("encipherencode", encipherencode);
      }

      ["line_item_id", "return_type"].forEach((key) => {
        const value = params.get(key);
        if (value) {
          productParams.set(key, value);
        }
      });

      const query = productParams.toString();
      const url = `${versionedPath("/products")}${query ? `?${query}` : ""}`;
      navigate(url);
    };

    const goToProductsWithoutServiceabilityCheck = () => {
      dispatch(updateServiceability(true));
      dispatch(addShipmentMethod("merchant_handles_return_shipment"));
      goToProducts();
    };

    if (order.delivered_at !== null) {
      goToProductsWithoutServiceabilityCheck();
      return;
    }

    deliveryStatusCheck(merchantConfig.merchant_id, order.order_id)
      .then((resp) => {
        const deliveredOrder = resp.data.orders[0];
        dispatch(updateSelectedOrder(deliveredOrder));
        goToProductsWithoutServiceabilityCheck();
      })
      .catch(() => {
        toast.error("The order has not been delivered yet.", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      });
  };

  useEffect(() => {
    if (
      Object.keys(merchantConfig).length === 0 ||
      (userPhone === null && userEmail === null)
    ) {
      if (userPhone == null && userEmail == null) {
        resetToLogin();
      }
      return;
    }

    if (accessToken !== null) {
      sessionStorage.setItem("access_token", accessToken);
    }

    if (
      shouldForwardRequestTarget(normalizedNextTarget, rid)
    ) {
      navigate(buildForwardedTargetUrl("/tracking"), { replace: true });
      return;
    }

    ordersList(0, undefined, orderId)
      .then((resp) => {
        const orders = resp.data.orders || [];
        setAllOrders(orders);
        dispatch(addOrders(orders));
        setOrdersLoading(false);
        setApiLoadError(false);
        setLoginLoader(false);

        if (orderId !== null) {
          if (userPhone != null) {
            dispatch(addPhone(userPhone));
          }
          if (userEmail !== null) {
            dispatch(addEmail(userEmail.toLowerCase()));
          }

          const preSelectedOrder = orders[0];

          if (preSelectedOrder?.eligible) {
            updateOrderDetails(preSelectedOrder);
            return;
          }

          toast.error(
            preSelectedOrder ? t("orders.notEligible") : t("orders.notFound"),
            {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
            },
          );
          return;
        }

        dispatch(updateSelectedOrder({}));
      })
      .catch(() => {
        if (userPhone === "" && userEmail === "") {
          resetToLogin();
        } else {
          setApiLoadError(true);
          setOrdersLoading(false);
        }
      });

    window.history.pushState(
      null,
      null,
      encipherencode
        ? `${window.location.pathname}?encipherencode=${encipherencode}`
        : window.location.pathname,
    );
    window.addEventListener("popstate", onBackButtonEvent);

    dispatch(addRefundSource(null));
    dispatch(addRefundJSON({}));

    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantConfig]);

  useEffect(() => {
    dispatch(addUploadMediaIds([]));
    dispatch(addReturnedProducts([]));
  }, [dispatch]);

  const filteredOrders = useMemo(() => {
    const ordersArray = Array.isArray(allOrders) ? allOrders : [];
    return sortOrdersByEligibility(
      filterOrdersByQuery(ordersArray, searchQuery),
    );
  }, [allOrders, searchQuery]);

  const renderOrders = () => {
    return (
      <StyledOrdersList
        primaryColor={merchantConfig.primary_theme_color}
        rounded={merchantConfig.use_rounded_edge_buttons}
      >
        <div className="orders-shell">
          <PageHeader
            logoSrc={merchantConfig.brand_logo}
            logoAlt={merchantConfig.shop_display_name || "Brand"}
            onBack={resetToLogin}
            primaryColor={merchantConfig.primary_theme_color}
            className={isSearchStuck ? "orders-header-stuck" : ""}
          />

          <main className="orders-content">
            <div className="order-wrapper">
              <PageIntro
                title="Your Orders"
                description="Select a product to manage returns, exchanges, or track status."
                className="orders-page-intro"
                padding="0"
              />

              {isSingleRequestPerOrderMerchant(merchantConfig.merchant_id) && (
                <p className="single-request-notice">
                  {singleRequestPerOrderNotice}
                </p>
              )}
            </div>

            <OrdersSearch
              value={searchQuery}
              onChange={setSearchQuery}
              isStuck={isSearchStuck}
              searchShellRef={searchShellRef}
            />

            {apiLoadError ? (
              <div className="empty-state">We couldn't load your orders.</div>
            ) : ordersLoading ? (
              <V2OrdersBoneyardLoader />
            ) : filteredOrders.length > 0 ? (
              <div className="orders-list">
                {filteredOrders.map((order, index) => (
                  <OrderCard
                    key={order.order_id || order.order_number || index}
                    order={order}
                    rmsConfig={rmsConfig}
                    onSelect={updateOrderDetails}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">No orders found for this search.</div>
            )}
          </main>
        </div>
      </StyledOrdersList>
    );
  };

  return renderOrders();
}

export default OrdersList;
